
import { useState, useEffect, useRef } from 'react';
import PrimaryButton from '../../components/button/PrimaryButton';
import { useSelector } from 'react-redux';
import CircularProgress from '../../components/loader/CircularProgress';
import BackButton from '../../components/button/BackButton';
import { useNavigate, useLocation } from 'react-router-dom';
import { ACCOUNT, CREATE_FID_CARD_CASHBACK } from '../../constants/routes';
import { calculateProgress } from '../../utils/userInfoProgress';
import { isValidName, isValidGender, AVAILABLE_GENDERS } from '../../utils/Validator';
import useAxios from '../../axios/useAxios';
import { notifyError, notifySuccess } from '../../utils/Notify';
import DeleteAccountModal from '../../components/card/DeleteAccountModal';
import { findUser, logout, handleUserUpdate, handleAccountDeletion } from '../../actions/userActions';
import PageLoader from '../../components/loader/PageLoader';
import StoreSearchStep from './fid/add-card/steps/StoreSearchStep';
import { useDispatch } from 'react-redux';
import {
  TextInput,
  EmailInput,
  DateInput,
  GenderSelector,
  FormSection,
  AddressInputGroup,
} from '../../components/inputs';
import { PhoneInputComponent } from '../../components/phoneLogin/PhoneInput';
import {
  INITIAL_FORM_STATE,
  INITIAL_COMPLETED_SECTIONS,
  getMinimumBirthDate,
  checkCompletedSections,
  transformFormDataToFlat,
  initializeFormDataFromUser,
  createFieldChangeHandler,
  createBackHandler,
  scrollToTop,
  getMaximumBirthDate,
} from '../../utils/updateUserFonctions';

const UpdateUserScreen = () => {
  const user = useSelector((state) => state.user);
  const formRef = useRef(null);
  const { store } = useSelector((state) => state.store);
  const navigate = useNavigate();
  const location = useLocation();
  const axiosInstance = useAxios();
  const dispatch = useDispatch();
  
  const storeIdFromNavigation = location.state?.storeId;
  const previousRoute = location.state?.previousRoute;
  
  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isValidBirthDate, setIsValidBirthDate] = useState(false);
  const [isValidAddress, setIsValidAddress] = useState(true);
  const [isValidGenderValue, setIsValidGenderValue] = useState(true);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [completedSections, setCompletedSections] = useState(INITIAL_COMPLETED_SECTIONS);
  
  const minimumBirthDate = getMinimumBirthDate();
  const maximumBirthDate = getMaximumBirthDate();
  const formDataPlat = transformFormDataToFlat(formData);

  useEffect(() => {
    if (user?._id) {
      setCompletedSections(checkCompletedSections(formData, minimumBirthDate));
    } else {
      navigate(ACCOUNT);
    }
  }, [formData, user, navigate]);

  // Handlers
  const handleChange = createFieldChangeHandler(setFormData);
  const handleBack = createBackHandler(navigate, previousRoute, ACCOUNT);

  // Initialize form
  useEffect(() => {
    scrollToTop(formRef);
    if (user?._id) {
      setFormData(initializeFormDataFromUser(user));
    }
  }, [user]);

  // Validate gender whenever it changes
  useEffect(() => {
    if (formData.genre) {
      setIsValidGenderValue(isValidGender(formData.genre));
    } else {
      setIsValidGenderValue(true); // Empty is valid for optional field
    }
  }, [formData.genre]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?._id) {
      navigate(ACCOUNT);
      return;
    }
    await dispatch(handleUserUpdate(formDataPlat, axiosInstance, navigate, previousRoute));
  };

  // Handle account deletion
  const deleteAccount = async (reason_id, reason_label, details) => {
    await dispatch(handleAccountDeletion(reason_id, reason_label, details, axiosInstance, navigate));
  };

    


  return user?._id ? (
    <div className="update-user-container" ref={formRef}>
      <BackButton isAbsolute={true} onClick={handleBack} />

      <div className="form-header" style={{ '--delay': '0s' }}>
        <h1>Mon compte</h1>
        <CircularProgress
          percentage={calculateProgress(formData)}
          user={user}
        />
      </div>
      <form onSubmit={handleSubmit}>
        {/* Section User info */}
        <FormSection
          emoji="👤"
          title="Coordonnées"
          isCompleted={completedSections.coordonnees}
          percentage={30}
          isOptional={true}
          delay="0.5s"
        >
          <TextInput
            placeholder="Nom"
            maxLength={30}
            value={formData.coordonnees.nom}
            onChange={(value) => handleChange('coordonnees', 'nom', value)}
            isValid={isValidName(formData.coordonnees?.nom)}
          />

          <TextInput
            placeholder="Prénom"
            maxLength={30}
            value={formData.coordonnees.prenom}
            onChange={(value) => handleChange('coordonnees', 'prenom', value)}
            isValid={isValidName(formData.coordonnees?.prenom)}
          />

          <EmailInput
            value={formData.coordonnees.email}
            onChange={(value) => {handleChange('coordonnees', 'email', value)}}
            onValidationChange={setIsValidEmail}
            isValid={isValidEmail}
            disabled={user.email}
          />
        </FormSection>

        {/* Section Gender */}
        <FormSection
          emoji="🪢"
          title="Genre"
          isCompleted={completedSections.genre && isValidGenderValue}
          percentage={10}
          delay="1s"
          isOptional={true}
        >
          {formData.genre && !isValidGenderValue && (
            <p className="text-[#FF2E6C] text-xl geomanistRegular font-semibold mt-2">
              Choisis ton genre.
            </p>
          )}
          <GenderSelector
            value={formData.genre}
            onChange={(value) => handleChange('genre', '', value)}
          />
        </FormSection>

        {/* Section birthday */}
        <FormSection
          emoji="🎂"
          title="Date de naissance"
          isCompleted={completedSections.dateNaissance}
          percentage={10}
          delay="1.5s"
          isOptional={true}
        >
          <DateInput
            value={formData.dateNaissance}
            onChange={(value) => handleChange('dateNaissance', '', value)}
            onValidationChange={setIsValidBirthDate}
            max={minimumBirthDate.toISOString().split('T')[0]}
            min={maximumBirthDate.toISOString().split('T')[0]}
            isValid={isValidBirthDate}
          />
        </FormSection>

        {/* Section Adresse */}
        <FormSection
          emoji="📍"
          title="Adresse postale"
          isCompleted={completedSections.adresse}
          isOptional={true}
          delay="2s"
          className="relative z-20"
        >
          <AddressInputGroup
            value={formData.adresse}
            onChange={(field, value) => handleChange('adresse', field, value)}
            onValidationChange={setIsValidAddress}
          />
        </FormSection>

        {/* Section Phone */}
        <FormSection
          emoji="📞"
          title="Téléphone"
          isCompleted={completedSections.telephone}
          percentage={10}
          isValid={isValidPhoneNumber}
          delay="2.5s"
        >
          <PhoneInputComponent
            value={formData.telephone}
            onChange={(value) => handleChange('telephone', '', value)}
            onValidationChange={setIsValidPhoneNumber}
            isValid={isValidPhoneNumber}
            displayCheckMark={isValidPhoneNumber}
            onlyFrenchNumbers={true}
            disabled={user.phoneNumber}
          />
        </FormSection>

        {/* Section Favorite store */}
        <FormSection
          emoji="🏠"
          title="Magasin préféré"
          isCompleted={completedSections.favoriteStore}
          percentage={10}
          delay="3s"
        >
          {!formData.favoriteStore && (
            <p className="text-red-400 text-xl geomanistRegular font-semibold">
              Ton magasin préféré est obligatoire pour continuer.
            </p>
          )}

          <StoreSearchStep
            value={formData.favoriteStore}
            onChange={(value) => handleChange('favoriteStore', '', value)}
            error={''}
            storeId={storeIdFromNavigation ? storeIdFromNavigation : store?.id}
            viewNextButton={false}
          />
        </FormSection>

        {/* Delete account modal */}
        <DeleteAccountModal onConfirm={deleteAccount} />
      </form>

      {/* Submit button */}
      <div className="submit-button-container">
        <PrimaryButton 
          disabled={
            !isValidName(formData.coordonnees?.nom) ||
            !isValidName(formData.coordonnees?.prenom) ||
            !isValidEmail ||
            !isValidBirthDate ||
            !isValidAddress ||
            (formData.genre && !isValidGenderValue) ||
            (formData.telephone && !isValidPhoneNumber)
          } 
          label={'Enregistrer'} 
          onClick={handleSubmit} 
        />
      </div>
    </div>
  ) : (
    <PageLoader />
  );
};

export default UpdateUserScreen;
